import { useEffect, useRef, useState, type ReactElement } from 'react'

/**
 * Available catalog options for the dropdown.
 * Each catalog represents a different storefront catalog in SFCC.
 */
export const CATALOG_OPTIONS = ['cnv-storefront-catalog-us', 'cnv-storefront-catalog-uk'] as const

export type CatalogId = (typeof CATALOG_OPTIONS)[number]

export const DEFAULT_CATALOG_ID: CatalogId = 'cnv-storefront-catalog-us'

interface CatalogSelectorProps {
    readonly selectedCatalogId: string
    readonly onCatalogChange: (catalogId: string) => Promise<void>
    readonly disabled?: boolean
    readonly onError?: (error: string) => void
}

export function CatalogSelector({
    selectedCatalogId,
    onCatalogChange,
    disabled = false,
    onError
}: CatalogSelectorProps): ReactElement {
    const [isOpen, setIsOpen] = useState(false)
    const [focusedIndex, setFocusedIndex] = useState(-1)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLUListElement>(null)

    const selectedCatalog = CATALOG_OPTIONS.find((catalog) => catalog === selectedCatalogId)
    const selectedIndex = CATALOG_OPTIONS.findIndex((catalog) => catalog === selectedCatalogId)

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    // Close dropdown on Escape key
    useEffect(() => {
        function handleEscape(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                setIsOpen(false)
                setFocusedIndex(-1)
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
            return () => document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen])

    // Set initial focus when dropdown opens
    useEffect(() => {
        if (isOpen) {
            // Focus the currently selected item, or first item if none selected
            setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0)
        } else {
            setFocusedIndex(-1)
        }
    }, [isOpen, selectedIndex])

    const handleSelect = async (catalogId: string): Promise<void> => {
        try {
            await onCatalogChange(catalogId)
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to change catalog'
            onError?.(message)
            console.error('Catalog change failed:', err)
        } finally {
            setIsOpen(false)
            setFocusedIndex(-1)
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent): void => {
        if (!isOpen) {
            // Open dropdown on Enter, Space, or arrow keys when closed
            if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
                event.preventDefault()
                setIsOpen(true)
            }
            return
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                setFocusedIndex((prev) => (prev < CATALOG_OPTIONS.length - 1 ? prev + 1 : 0))
                break
            case 'ArrowUp':
                event.preventDefault()
                setFocusedIndex((prev) => (prev > 0 ? prev - 1 : CATALOG_OPTIONS.length - 1))
                break
            case 'Enter':
            case ' ':
                event.preventDefault()
                {
                    const catalog = CATALOG_OPTIONS[focusedIndex]
                    if (catalog !== undefined) {
                        void handleSelect(catalog)
                    }
                }
                break
            case 'Home':
                event.preventDefault()
                setFocusedIndex(0)
                break
            case 'End':
                event.preventDefault()
                setFocusedIndex(CATALOG_OPTIONS.length - 1)
                break
            case 'Tab':
                // Close dropdown on Tab, let default behavior happen
                setIsOpen(false)
                setFocusedIndex(-1)
                break
        }
    }

    return (
        <div className='flex flex-col gap-2'>
            <label id='catalog-selector-label' className='text-md font-medium text-white'>
                Category Catalog
            </label>
            <p className='text-xs text-slate-400'>
                Select the SFCC catalog to search categories from
            </p>
            <div className='relative' ref={dropdownRef}>
                {/* Dropdown trigger button */}
                <button
                    type='button'
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    aria-haspopup='listbox'
                    aria-expanded={isOpen}
                    aria-labelledby='catalog-selector-label'
                    aria-activedescendant={
                        isOpen && focusedIndex >= 0 ? `catalog-option-${focusedIndex}` : undefined
                    }
                    className='flex w-full items-center justify-between rounded-md border border-slate-700 bg-slate-950 px-4 py-2.5 text-left text-sm font-medium text-slate-100 transition-colors focus:border-sky-500 focus:ring-2 focus:ring-sky-600 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50'
                >
                    <span>{selectedCatalog ?? 'Select a catalog'}</span>
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className={`h-5 w-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        viewBox='0 0 20 20'
                        fill='currentColor'
                        aria-hidden='true'
                    >
                        <path
                            fillRule='evenodd'
                            d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'
                            clipRule='evenodd'
                        />
                    </svg>
                </button>

                {/* Dropdown menu - positioned below, constrained to button width */}
                {isOpen && (
                    <ul
                        ref={listRef}
                        role='listbox'
                        aria-labelledby='catalog-selector-label'
                        className='absolute top-full right-0 left-0 z-50 m-0 mt-1 overflow-hidden rounded-md border border-slate-700 bg-slate-900 p-0 shadow-lg'
                    >
                        {CATALOG_OPTIONS.map((catalog, index) => {
                            const isSelected = catalog === selectedCatalogId
                            const isFocused = index === focusedIndex
                            return (
                                <li
                                    key={catalog}
                                    id={`catalog-option-${index}`}
                                    role='option'
                                    aria-selected={isSelected}
                                    onClick={() => void handleSelect(catalog)}
                                    className={`cursor-pointer list-none px-4 py-2.5 text-sm font-medium transition-colors ${
                                        isSelected
                                            ? 'bg-sky-500/20 text-sky-200'
                                            : isFocused
                                              ? 'bg-slate-800 text-slate-100'
                                              : 'text-slate-100 hover:bg-slate-800'
                                    }`}
                                >
                                    <div className='flex items-center justify-between'>
                                        <span>{catalog}</span>
                                        {isSelected && (
                                            <svg
                                                xmlns='http://www.w3.org/2000/svg'
                                                className='h-4 w-4 text-sky-400'
                                                viewBox='0 0 20 20'
                                                fill='currentColor'
                                            >
                                                <path
                                                    fillRule='evenodd'
                                                    d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                                                    clipRule='evenodd'
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        </div>
    )
}

export default CatalogSelector
